import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import Particles from './Particles';

const Hero = () => {
  const targetRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section 
      ref={targetRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-white via-gray-50 to-blue-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-colors duration-300"
    >
      <Particles />
      
      <motion.div
        style={{ y, opacity }}
        className="container mx-auto px-4 py-20 relative z-10"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-4xl mx-auto"
        >
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-5xl md:text-7xl font-display font-bold mb-6 leading-tight pb-1"
          >
            <span className="text-gray-800 dark:text-white transition-colors duration-300">
              AI & Software
            </span>
            <br />
            <span className="bg-gradient-to-r from-blue-800 to-blue-600 bg-clip-text text-transparent transition-colors duration-300">
              Engineer
            </span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl md:text-2xl mb-8 text-gray-600 dark:text-gray-300 transition-colors duration-300"
          >
            AI infrastructure and automation that turn complex challenges into intelligent solutions
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-6 justify-center"
          >
            <motion.a
              href="#portfolio"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-8 py-3 bg-blue-700 dark:bg-blue-600 text-white font-medium rounded-lg transition-all hover:bg-blue-800 dark:hover:bg-blue-700 hover:shadow-lg"
            >
              View Portfolio
            </motion.a>
            
            <motion.a
              href="https://www.upwork.com/freelancers/~01902b7448e204d256?mp_source=share"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-8 py-3 bg-white dark:bg-transparent border-2 border-blue-700 dark:border-blue-600 text-blue-700 dark:text-blue-600 font-medium rounded-lg transition-all hover:bg-blue-50 dark:hover:bg-blue-900/20"
            >
              Contact on Upwork
            </motion.a>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1 }}
        className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 12, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="text-gray-400 dark:text-gray-500 transition-colors duration-300 text-4xl font-light"
        >
          ↓
        </motion.div>
      </motion.div>

      {/* AIChat is mounted at the root layout to avoid duplication */}
    </section>
  );
};

export default Hero; 